import React, { useCallback, useState } from 'react';
import useFileUpload from '../../hooks/useFileUpload';
import Button from '../Button/Button';
import { Box } from 'rebass';
import { useMutation } from '@apollo/client';
import useNotify from '../../hooks/useNotify';
import { useUploadTempFile } from '../../hooks/useUploadTempFile';
import { gql } from '@/modes/waterSupplier/gql';

const ADD_WATER_SERVICE_ATTACHMENT = gql(`
  mutation AddWaterServiceAttachment(
    $waterServiceId: ID!
    $tempKey: String!
    $contentType: String!
  ) {
    addWaterServiceAttachment(
      waterServiceId: $waterServiceId
      tempKey: $tempKey
      contentType: $contentType
    ) {
      id
    }
  }
`);

type WaterServicePhotosEditProps = {
  waterServiceId: string;
  onPhotosAdded?: (() => void) | (() => Promise<any>);
};

const accept = {
  'image/*': ['.jpg', '.jpeg', '.png', '.webp'],
  'application/pdf': ['.pdf'],
};

const WaterServicePhotosEdit: React.FC<WaterServicePhotosEditProps> = ({
  waterServiceId,
  onPhotosAdded,
}) => {
  const { showError } = useNotify();

  const [addWaterServiceAttachment] = useMutation<
    { addWaterServiceAttachment: { id: string } },
    { waterServiceId: string; tempKey: string; contentType: string }
  >(ADD_WATER_SERVICE_ATTACHMENT, {
    onError: (error) => {
      showError('Error attaching photo to water service', error);
    },
  });

  const [uploading, setUploading] = useState<boolean>(false);

  const { uploadTempFiles } = useUploadTempFile();

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      try {
        setUploading(true);
        const uploadResults = await uploadTempFiles(acceptedFiles);

        const promises = uploadResults.map((tempFile) => {
          return addWaterServiceAttachment({
            variables: {
              waterServiceId,
              contentType: tempFile.contentType,
              tempKey: tempFile.key,
            },
          });
        });

        await Promise.all(promises);
        onPhotosAdded?.();
      } catch (e) {
        showError('Error uploading files', e as Error);
      } finally {
        setUploading(false);
      }
    },
    [
      uploadTempFiles,
      waterServiceId,
      addWaterServiceAttachment,
      onPhotosAdded,
      showError,
    ]
  );

  const { getRootProps, getInputProps } = useFileUpload({
    multiple: true,
    accept,
    onDrop,
    disabled: uploading,
  });

  return (
    <>
      <Box flexShrink={0} flexGrow={0} {...(getRootProps() as any)}>
        <input {...getInputProps()} />
        <Button icon="image" variant="secondary" disabled={uploading}>
          {uploading ? 'Uploading...' : 'Select...'}
        </Button>
      </Box>
    </>
  );
};

export default WaterServicePhotosEdit;
